import { gsap } from "gsap";
import { Container, Sprite, Texture } from "pixi.js";

import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { ApocalypseFontsNames } from "../../../assetsLoader/ApocalypseAssetsNames";

export class ApocalypseIntroText extends Container {
    private spinsLeftText: AbstractBitmapText;
    private spinsLeftBackground: Sprite;

    private spinsNameSprite: Sprite;
    private spinsNameBackground: Sprite;

    private spinsTextSprite: Sprite;
    private spinsTextBackground: Sprite;

    private fsLeft: number;
    private showSpinsLeft: boolean = true;

    constructor(fsLeft: number, showSpinsLeft: boolean = true) {
        super();

        this.fsLeft = fsLeft;
        this.showSpinsLeft = showSpinsLeft;

        this.init();
    }

    public init(): void {
        if (this.showSpinsLeft) {
            this.spinsLeftBackground = generateScaledSprite(`apocalypse/fs_shadowtext_3`);
            this.spinsLeftBackground.name = `spinsLeftBackground`;
            this.spinsLeftBackground.anchor.set(0.5);
            this.spinsLeftBackground.y = -202;
            this.addChild(this.spinsLeftBackground);

            this.spinsLeftText = new AbstractBitmapText({
                text: this.fsLeft.toString(),
                style: {
                    fontName: ApocalypseFontsNames.APOCALYPSE_RED,
                    fontSize: 22,
                },
            });
            this.spinsLeftText.name = `spinsLeftText`;
            this.spinsLeftText.y = -214;
            this.addChild(this.spinsLeftText);
        }

        this.spinsNameBackground = generateScaledSprite(`apocalypse/fs_shadowtext_1`);
        this.spinsNameBackground.name = `spinsNameBackground`;
        this.spinsNameBackground.anchor.set(0.5);
        this.spinsNameBackground.y = -49;

        this.addChild(this.spinsNameBackground);

        this.spinsNameSprite = generateScaledSprite(`apocalypse/carnage_spin`);
        this.spinsNameSprite.name = `spinsNameSprite`;
        this.spinsNameSprite.anchor.set(0.5);
        this.spinsNameSprite.y = -51;

        this.addChild(this.spinsNameSprite);

        this.spinsTextBackground = generateScaledSprite(`apocalypse/fs_shadowtext_2`);
        this.spinsTextBackground.name = `spinsTextBackground`;
        this.spinsTextBackground.anchor.set(0.5);
        this.spinsTextBackground.y = 50;

        this.addChild(this.spinsTextBackground);

        this.spinsTextSprite = generateScaledSprite(`apocalypse/fs_spins`);
        this.spinsTextSprite.name = `spinsTextSprite`;
        this.spinsTextSprite.anchor.set(0.5);
        this.spinsTextSprite.y = 49;

        this.addChild(this.spinsTextSprite);
    }

    public updateSpinsLeftText(fsLeft: number): void {
        this.fsLeft = fsLeft;
        this.spinsLeftText?.updateValue(this.fsLeft.toString());
    }

    public getAnimation(isRestore: boolean, gameMode: AbstractGameMode): gsap.core.Timeline {
        const tl = gsap.timeline({ autoRemoveChildren: true, smoothChildTiming: true });

        this.spinsNameBackground.scale.set(0.8);
        this.spinsNameBackground.alpha = 0;

        const showSpineNameBackgroundAnimation = gsap.to(this.spinsNameBackground, {
            duration: 0.5,
            pixi: { scale: 0.5 },
            alpha: 1,
            ease: `power1.in`,
        });

        tl.add(showSpineNameBackgroundAnimation, 0);

        switch (gameMode) {
            case AbstractGameMode.SUPER_FREESPIN:
                this.spinsNameSprite.texture = Texture.from(`apocalypse/doom_spin`);
                break;

            case AbstractGameMode.FREESPIN_BETTER:
                this.spinsNameSprite.texture = Texture.from(`apocalypse/cataclysm_spin`);
                break;

            default:
                this.spinsNameSprite.texture = Texture.from(`apocalypse/carnage_spin`);
                break;
        }
        this.spinsNameSprite.scale.set(0.8);
        this.spinsNameSprite.alpha = 0;

        const showSpineNameAnimation = gsap.to(this.spinsNameSprite, {
            duration: 0.8,
            pixi: { scale: 0.5 },
            alpha: 1,
            ease: `elastic.out(1,0.3)`,
        });

        tl.add(showSpineNameAnimation, 0.1);

        this.spinsTextBackground.scale.set(0.8);
        this.spinsTextBackground.alpha = 0;

        const showSpineTextBackgroundAnimation = gsap.to(this.spinsTextBackground, {
            duration: 0.5,
            pixi: { scale: 0.5 },
            alpha: 1,
            ease: `power1.in`,
        });

        tl.add(showSpineTextBackgroundAnimation, 0.1);

        this.spinsTextSprite.scale.set(0.8);
        this.spinsTextSprite.alpha = 0;

        const showSpineTextAnimation = gsap.to(this.spinsTextSprite, {
            duration: 0.8,
            pixi: { scale: 0.5 },
            alpha: 1,
            ease: `elastic.out(1,0.3)`,
        });

        tl.add(showSpineTextAnimation, 0.2);

        if (this.showSpinsLeft) {
            this.spinsLeftBackground.scale.set(0.8);
            this.spinsLeftBackground.alpha = 0;

            const showSpinsLeftBackgroundAnimation = gsap.to(this.spinsLeftBackground, {
                duration: 0.5,
                pixi: { scale: 0.5 },
                alpha: 1,
                ease: `power1.in`,
            });

            tl.add(showSpinsLeftBackgroundAnimation, 0.1);

            this.spinsLeftText.alpha = 0;
            this.spinsLeftText.scale.set(0.8);

            const showSpinsLeftTextAnimation = gsap.to(this.spinsLeftText, {
                duration: 0.8,
                pixi: { scale: 0.5 },
                alpha: 1,
                ease: `elastic.out(1,0.3)`,
            });

            tl.add(showSpinsLeftTextAnimation, 0.2);
        }

        const tlEndLabel = `tlEndLabel`;
        tl.addLabel(tlEndLabel, `>`);

        if (isRestore) {
            tl.seek(tl.labels[tlEndLabel], false);
        }

        return tl;
    }
}
